import React, { useEffect, useState } from "react";
import {
  adminFetchBtwEmailTemplates,
  adminCreateBtwEmailTemplate,
  adminUpdateBtwEmailTemplate,
  adminArchiveBtwEmailTemplate,
} from "../../core/axios/endpoints";
import ConfirmModal from "../../components/confirm-modal";

const emptyForm = { name: "", subject: "", body: "" };

const BtwEmailTemplatesPage = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [editing, setEditing] = useState(null); // template-object of "new"
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminFetchBtwEmailTemplates({
        includeArchived: showArchived,
      });
      setTemplates(res?.data ?? res ?? []);
    } catch (e) {
      console.error("Templates laden mislukt:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [showArchived]);

  const startNew = () => {
    setEditing("new");
    setForm(emptyForm);
    setError(null);
  };

  const startEdit = (tpl) => {
    setEditing(tpl._id);
    setForm({ name: tpl.name, subject: tpl.subject, body: tpl.body });
    setError(null);
  };

  const cancel = () => {
    setEditing(null);
    setForm(emptyForm);
    setError(null);
  };

  const save = async () => {
    if (!form.name.trim() || !form.subject.trim() || !form.body.trim()) {
      setError("Vul alle velden in.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (editing === "new") {
        await adminCreateBtwEmailTemplate(form);
      } else {
        await adminUpdateBtwEmailTemplate(editing, form);
      }
      await load();
      cancel();
    } catch (e) {
      setError(e?.response?.data?.error ?? e?.message ?? "Opslaan mislukt.");
    } finally {
      setSaving(false);
    }
  };

  const doArchive = async () => {
    if (!archiveTarget) return;
    setSaving(true);
    try {
      await adminArchiveBtwEmailTemplate(
        archiveTarget._id,
        !archiveTarget.archived,
      );
      setArchiveTarget(null);
      await load();
    } catch (e) {
      alert(e?.response?.data?.error ?? "Mislukt.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page-wrapper px-6 pt-24 pb-12">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold tracking-tight text-gray-900">
            Mail-templates
          </h2>
          <p className="mt-1 text-xs text-gray-500">
            Sjablonen die in de &quot;Mail naar klant&quot; modal verschijnen als
            quick-pick knoppen.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300"
            />
            Toon gearchiveerd
          </label>
          <button
            type="button"
            onClick={startNew}
            disabled={editing !== null}
            className="rounded-md bg-gray-900 px-4 py-1.5 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50"
          >
            + Nieuwe template
          </button>
        </div>
      </div>

      {/* Editor */}
      {editing !== null ? (
        <div className="mb-6 rounded-xl border border-emerald-200 bg-emerald-50/30 p-5 shadow-sm">
          <h3 className="mb-3 text-sm font-semibold text-gray-900">
            {editing === "new" ? "Nieuwe template" : "Template bewerken"}
          </h3>
          <div className="space-y-3">
            <label className="block text-sm">
              <span className="font-medium text-gray-700">
                Naam <span className="text-xs text-gray-500">(quick-pick label)</span>
              </span>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Bijv. Aanvullende documenten nodig"
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                disabled={saving}
              />
            </label>
            <label className="block text-sm">
              <span className="font-medium text-gray-700">Onderwerp</span>
              <input
                type="text"
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                placeholder="Onderwerp van de mail"
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                disabled={saving}
              />
            </label>
            <label className="block text-sm">
              <span className="font-medium text-gray-700">
                Bericht <span className="text-xs text-gray-500">(lege regels = nieuwe alinea)</span>
              </span>
              <textarea
                rows={8}
                value={form.body}
                onChange={(e) => setForm({ ...form, body: e.target.value })}
                placeholder="Schrijf hier de standaard tekst. Gebruik [vul aan] als placeholder voor variabele delen."
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                disabled={saving}
              />
            </label>
            {error ? (
              <div className="rounded-md border border-rose-300 bg-rose-50 p-3 text-sm text-rose-900">
                {error}
              </div>
            ) : null}
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={cancel}
                disabled={saving}
                className="rounded-md border border-gray-300 bg-white px-4 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Annuleren
              </button>
              <button
                type="button"
                onClick={save}
                disabled={saving}
                className="rounded-md bg-emerald-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
              >
                {saving ? "Opslaan…" : "Opslaan"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Lijst */}
      <div className="overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-gray-200">
        {loading ? (
          <div className="p-10 text-center text-gray-500">Laden…</div>
        ) : templates.length === 0 ? (
          <div className="p-10 text-center text-gray-500">
            {showArchived ? "Geen templates." : "Geen actieve templates. Maak er een aan."}
          </div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {templates.map((tpl) => (
              <li key={tpl._id} className="flex items-start gap-4 p-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium text-gray-900">{tpl.name}</h3>
                    {tpl.archived ? (
                      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-600">
                        Gearchiveerd
                      </span>
                    ) : null}
                  </div>
                  <p className="mt-1 text-sm text-gray-700">{tpl.subject}</p>
                  <p className="mt-1 line-clamp-2 text-xs text-gray-500">
                    {tpl.body}
                  </p>
                </div>
                <div className="flex flex-shrink-0 items-center gap-1">
                  <button
                    type="button"
                    onClick={() => startEdit(tpl)}
                    disabled={editing !== null}
                    className="rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Bewerken
                  </button>
                  <button
                    type="button"
                    onClick={() => setArchiveTarget(tpl)}
                    disabled={editing !== null}
                    className="rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    {tpl.archived ? "Terughalen" : "Archiveren"}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <ConfirmModal
        isOpen={!!archiveTarget}
        title={archiveTarget?.archived ? "Terughalen?" : "Archiveren?"}
        message={
          archiveTarget?.archived
            ? `"${archiveTarget?.name}" wordt weer beschikbaar als quick-pick.`
            : `"${archiveTarget?.name}" verdwijnt uit de quick-picks. Bestaande mails blijven gewoon.`
        }
        confirmLabel={archiveTarget?.archived ? "Terughalen" : "Archiveren"}
        cancelLabel="Annuleren"
        busy={saving}
        onConfirm={doArchive}
        onCancel={() => setArchiveTarget(null)}
      />
    </div>
  );
};

export default BtwEmailTemplatesPage;
